import { ThreeRowsStyles } from "./LotInfoBarStyles";
import Spinner from "components/SimpleComponents/Spinner/Spinner";
import type { LotInfoType } from "types/types";
import { TextField } from "@mui/material";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { startCase } from "lodash";
import { useLotText } from "./lotInfoUtils";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useIsDarkMode } from "store/atoms";

const EMPTY_INPUT_STRING = "--";

const OutlinedTextField = props => (
  <TextField
    className="textField"
    size="small"
    variant="outlined"
    InputLabelProps={{ shrink: true }}
    {...props}
  />
);

export function LotNumberAndNextLotNumber({
  width,
  isEditMode,
  editedLotInfo,
  handleTextInputChange,
  isLoading,
  isLotNumberError,
  lotInfo,
}: {
  width: number;
  isEditMode: boolean;
  editedLotInfo: LotInfoType;
  handleTextInputChange: (event: any) => void;
  isLoading: boolean;
  isLotNumberError: boolean;
  lotInfo: LotInfoType;
}) {
  const lotText = useLotText();
  const { translate } = useFlightTranslation();
  const lotNumberHelperText = isLotNumberError
    ? `cannot equal current ${startCase(lotText)} number`
    : null;

  const [isDarkMode] = useIsDarkMode();
  return (
    <ThreeRowsStyles
      data-server-names={[SERVER_NAMES.midgardLotInfo]}
      $width={width}
      className="left"
      $isEditMode={isEditMode}
      $isDarkMode={isDarkMode}
    >
      {isEditMode ? (
        <>
          <OutlinedTextField
            error={isLotNumberError}
            name="lotNumber"
            label={`${lotText} #`}
            type="text" // lot number can be e.g. "2793-90A"
            value={editedLotInfo?.lotNumber || ""}
            onChange={handleTextInputChange}
          />
          <OutlinedTextField
            error={isLotNumberError}
            helperText={lotNumberHelperText}
            name="nextLotNumber"
            label={translate(
              `Next_${startCase(lotText)}`,
              `Next ${startCase(lotText)} #`
            )}
            type="text" // lot number can be e.g. "2793-90A"
            value={editedLotInfo?.nextLotNumber || ""}
            onChange={handleTextInputChange}
          />
        </>
      ) : (
        <>
          <div className="row row-top">
            {translate(`${startCase(lotText)}_Number`)}
          </div>
          <div className="row row-middle">
            <LotInfo
              isLoading={isLoading}
              showSpinner={true}
              info={lotInfo.lotNumber}
            />
          </div>
          <div className="row row-bottom">
            {translate("next")}
            {" : "}
            <LotInfo isLoading={isLoading} info={lotInfo.nextLotNumber} />
          </div>
        </>
      )}
    </ThreeRowsStyles>
  );
}

export function LotSizeAndNextLotSize({
  width,
  isEditMode,
  editedLotInfo,
  handleNumberInputChange,
  isLoading,
  lotInfo,
}: {
  width: number;
  isEditMode: boolean;
  editedLotInfo: LotInfoType;
  handleNumberInputChange: (event: any) => void;
  isLoading: boolean;
  lotInfo: LotInfoType;
}) {
  const lotText = useLotText();
  const { translate } = useFlightTranslation();
  const [isDarkMode] = useIsDarkMode();

  return (
    <ThreeRowsStyles
      data-server-names={[SERVER_NAMES.midgardLotInfo]}
      $width={width}
      className="right"
      $isEditMode={isEditMode}
      $isDarkMode={isDarkMode}
    >
      {isEditMode ? (
        <>
          <OutlinedTextField
            name="lotSize"
            label={translate(`${startCase(lotText)}_Size`)}
            type="number"
            value={editedLotInfo.lotSize || ""}
            onChange={handleNumberInputChange}
          />
          <OutlinedTextField
            name="nextLotSize"
            label={translate(`Next_${startCase(lotText)}_Size`)}
            type="number"
            value={editedLotInfo.nextLotSize || ""}
            onChange={handleNumberInputChange}
          />
        </>
      ) : (
        <>
          <div className="row row-top">
            {translate(`${startCase(lotText)}_Size`)}
          </div>
          <div className="row row-middle">
            <LotInfo
              isLoading={isLoading}
              showSpinner={true}
              info={lotInfo.lotSize}
            />
          </div>
          <div className="row row-bottom">
            {translate("next")}
            {" : "}
            <LotInfo isLoading={isLoading} info={lotInfo.nextLotSize} />
          </div>
        </>
      )}
    </ThreeRowsStyles>
  );
}

function LotInfo({
  isLoading,
  info,
  showSpinner = false,
}: {
  isLoading: boolean;
  info: string | number | null;
  showSpinner?: boolean;
}) {
  return isLoading ? (
    showSpinner ? (
      <Spinner hideDuration={500} width={10} />
    ) : null
  ) : (
    <>{info || EMPTY_INPUT_STRING}</>
  );
}
